/**
 * Folder list: first-page and delta sync (spec §7.1 + REALTIME.md §2), paging, previews,
 * thread rows, cursor and selection. Everything here mutates the root store's state.
 */
import { api, ApiError } from '$lib/api/client';
import type { ApiMessage, SyncCursor, SyncResponse } from '$lib/api/types';
import { db, kvGet, kvSet, parseRowKey, purgeFolder, rowKey } from '$lib/db';
import { buildThreadRows, singleRow, threadOf as threadOfRows } from '$lib/mail/threads';
import { sortRows } from '$lib/mail/sort';
import { parseUidSet } from '$lib/mail/uidset';
import type { FolderRec, MessageRow, ThreadRow } from '$lib/types';
import type { MailStore } from './mail.svelte';

export const PAGE_SIZE = 50;
export const PREVIEW_BATCH = 20;
export const RECONCILE_MIN_MS = 60_000;

export interface SyncOptions {
  /** Ask `/api/messages` for `uidSet` (forced) — also used when the delta path is unavailable. */
  reconcile?: boolean;
  /** Ignore the cursor and reload the first page. */
  full?: boolean;
}

const str = (v: string | number | null | undefined) => (v === null || v === undefined ? '' : String(v));

export class ListModule {
  loadingMore = false;
  previewAsked = new Set<string>();
  previewQueue: { folder: string; uid: number }[] = [];
  private previewBusy = false;
  private lastReconcile = new Map<string, number>();
  /** Set when `/api/sync` answers 404/501 (legacy bridge): stay on the first-page path. */
  deltaUnsupported = false;

  constructor(private readonly s: MailStore) {}

  // ---- rows ---------------------------------------------------------------------------------

  toRow(folder: string, m: ApiMessage & { preview?: string; snippet?: string }): MessageRow {
    const from = Array.isArray(m.from) ? m.from[0] : (m.from as { name?: string; address?: string } | undefined);
    const row: MessageRow = {
      key: rowKey(folder, m.uid),
      folder,
      uid: m.uid,
      seq: m.seq ?? 0,
      subject: m.subject ?? '',
      date: m.date,
      fromName: from?.name || '',
      fromAddr: from?.address || '',
      flags: m.flags ?? [],
      size: m.size ?? 0,
      hasAttachments: !!m.hasAttachments,
      atts: m.atts ?? [],
      list: m.list === true,
      toAddrs: (m.to || []).map((a) => a.address).filter(Boolean),
      receivedAt: m.receivedAt ?? undefined,
      messageId: m.messageId ?? null,
      inReplyTo: m.inReplyTo ?? null,
      references: m.references ?? [],
    };
    const snippet = m.snippet ?? m.preview;
    if (snippet) row.snippet = snippet;
    return row;
  }

  /** Keep cached enrichments the fresh row lacks; `listed` always comes from the fresh row. */
  mergeRow(fresh: MessageRow, cached: readonly MessageRow[] | Map<string, MessageRow>): MessageRow {
    const old = cached instanceof Map ? cached.get(fresh.key) : cached.find((r) => r.key === fresh.key);
    if (!old) return fresh;
    return {
      ...fresh,
      snippet: fresh.snippet ?? old.snippet,
      toAddrs: fresh.toAddrs?.length ? fresh.toAddrs : old.toAddrs,
      receivedAt: fresh.receivedAt ?? old.receivedAt,
      messageId: fresh.messageId ?? old.messageId,
      inReplyTo: fresh.inReplyTo ?? old.inReplyTo,
      references: fresh.references?.length ? fresh.references : old.references,
      listed: fresh.listed,
    };
  }

  async listedRows(folder: string): Promise<MessageRow[]> {
    return (await db.messages.where('folder').equals(folder).toArray()).filter((r) => r.listed !== false);
  }

  async loadListFromCache(folder: string): Promise<void> {
    this.s.msgs = sortRows(await this.listedRows(folder), this.s.sortMode);
  }

  async countLoaded(folder: string): Promise<void> {
    const n = (await this.listedRows(folder)).length;
    if (folder === this.s.currentFolder) this.s.folderLoaded = n;
  }

  // ---- cursors ------------------------------------------------------------------------------

  async getCursor(folder: string): Promise<SyncCursor | null> {
    const rec = await db.sync.get(folder);
    if (!rec?.uidValidity || !rec.uidNext || !rec.highestModseq) return null;
    return { uidValidity: rec.uidValidity, uidNext: rec.uidNext, highestModseq: rec.highestModseq };
  }

  async saveCursor(folder: string, c: Partial<SyncCursor>): Promise<void> {
    const prev = (await db.sync.get(folder)) ?? { folder };
    await db.sync.put({ ...prev, folder, ...c, lastSyncAt: Date.now() });
  }

  async dropCursor(folder: string): Promise<void> {
    await db.sync.delete(folder);
  }

  /** Rows, bodies and cursor of a folder (uidValidity change, `mailbox.resync`). */
  async wipeFolder(folder: string): Promise<void> {
    await purgeFolder(folder);
    await this.dropCursor(folder);
  }

  wantReconcile(folder: string, force = false): boolean {
    const last = this.lastReconcile.get(folder) ?? 0;
    if (!force && Date.now() - last < RECONCILE_MIN_MS) return false;
    this.lastReconcile.set(folder, Date.now());
    return true;
  }

  // ---- sync ---------------------------------------------------------------------------------

  /** Delta sync when a cursor exists, first page otherwise. Rethrows after noting the error. */
  async syncFolder(folder: string, opts: SyncOptions = {}): Promise<void> {
    const cursor = opts.full || this.deltaUnsupported ? null : await this.getCursor(folder);
    if (cursor) {
      try {
        await this.deltaSync(folder, cursor);
        return;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) this.deltaUnsupported = true;
        else throw err;
      }
    }
    await this.firstPage(folder, opts.reconcile === true);
  }

  private async firstPage(folder: string, forceReconcile: boolean): Promise<void> {
    const reconcile = this.wantReconcile(folder, forceReconcile);
    const r = await api.messages(folder, { limit: PAGE_SIZE, reconcile }).catch((err) => {
      this.s.noteSyncError(folder);
      throw err;
    });
    if (folder === this.s.currentFolder) {
      this.s.folderTotal = r.total ?? 0;
      if (this.s.lastSyncError?.folder === folder) this.s.lastSyncError = null;
    }
    const uv = str(r.uidValidity);
    const uvKey = `uv:${folder}`;
    const prevUv = (await kvGet(uvKey)) ?? (await db.sync.get(folder))?.uidValidity ?? null;
    if (prevUv && uv && prevUv !== uv) await this.wipeFolder(folder);
    if (uv) await kvSet(uvKey, uv);

    const cached = await db.messages.where('folder').equals(folder).toArray();
    const cachedMap = new Map(cached.map((c) => [c.key, c]));
    const fresh = r.messages.map((m) => this.mergeRow(this.toRow(folder, m), cachedMap));
    await db.messages.bulkPut(fresh);
    const pageUids = new Set(fresh.map((m) => m.uid));

    if (!fresh.length) {
      await purgeFolder(folder);
    } else if (r.uidSet) {
      const set = parseUidSet(r.uidSet);
      const gone = cached.filter((c) => c.listed !== false && !pageUids.has(c.uid) && !set.has(c.uid));
      if (gone.length) await this.deleteRows(gone.map((c) => c.key));
    } else {
      const minUid = Math.min(...fresh.map((m) => m.uid));
      const gone = cached.filter((c) => c.uid >= minUid && !pageUids.has(c.uid));
      if (gone.length) await this.deleteRows(gone.map((c) => c.key));
    }

    // Seed the delta cursor from the page when the bridge reports all three values.
    if (uv && r.uidNext !== undefined && r.uidNext !== null && r.modseq !== undefined && r.modseq !== null) {
      await this.saveCursor(folder, { uidValidity: uv, uidNext: str(r.uidNext), highestModseq: str(r.modseq) });
    }
    await this.afterSync(folder);
  }

  private async deltaSync(folder: string, cursor: SyncCursor): Promise<void> {
    const r = await api.sync(folder, cursor).catch((err) => {
      this.s.noteSyncError(folder);
      throw err;
    });
    if (folder === this.s.currentFolder && this.s.lastSyncError?.folder === folder) this.s.lastSyncError = null;
    await this.applySync(folder, cursor, r);
    await this.afterSync(folder);
  }

  /** Apply a `/api/sync` response (exported for tests and for the SSE sink). */
  async applySync(folder: string, cursor: SyncCursor, r: SyncResponse): Promise<void> {
    const uv = str(r.uidValidity);
    if (r.resync || (uv && uv !== cursor.uidValidity)) {
      await this.wipeFolder(folder);
      const rows = (r.new ?? []).map((m) => this.toRow(folder, m));
      await db.messages.bulkPut(rows);
      await kvSet(`uv:${folder}`, uv);
      await this.saveCursor(folder, { uidValidity: uv, uidNext: str(r.uidNext), highestModseq: str(r.highestModseq ?? '0') });
      this.updateCounters(folder, r);
      if (folder === this.s.currentFolder) this.s.folderTotal = r.exists ?? rows.length;
      return;
    }
    const cached = await db.messages.where('folder').equals(folder).toArray();
    const byKey = new Map(cached.map((c) => [c.key, c]));
    const fresh = (r.new ?? []).map((m) => this.mergeRow(this.toRow(folder, m), byKey));
    if (fresh.length) await db.messages.bulkPut(fresh);
    const freshUids = new Set(fresh.map((m) => m.uid));
    if (r.changed?.length) {
      const patched: MessageRow[] = [];
      for (const ch of r.changed) {
        const row = byKey.get(rowKey(folder, ch.uid));
        if (row && !freshUids.has(ch.uid)) patched.push({ ...row, flags: ch.flags });
      }
      if (patched.length) await db.messages.bulkPut(patched);
    }
    if (r.uidSet) {
      const set = parseUidSet(r.uidSet);
      const gone = cached.filter((c) => !freshUids.has(c.uid) && !set.has(c.uid));
      if (gone.length) await this.deleteRows(gone.map((c) => c.key));
    }
    // Without CONDSTORE flags cannot be diffed; too many new rows means the page top is
    // incomplete. Both are repaired by a plain first page (which also refreshes the cursor).
    if (r.condstore === false || r.moreNew) {
      await this.firstPage(folder, false);
    }
    await this.saveCursor(folder, { uidValidity: uv || cursor.uidValidity, uidNext: str(r.uidNext), highestModseq: str(r.highestModseq ?? cursor.highestModseq) });
    this.updateCounters(folder, r);
    if (folder === this.s.currentFolder && typeof r.exists === 'number') this.s.folderTotal = r.exists;
  }

  private updateCounters(folder: string, r: { exists?: number; unseen?: number }): void {
    const f = this.s.folders.find((x) => x.path === folder);
    if (!f) return;
    let changed = false;
    if (typeof r.exists === 'number' && f.messages !== r.exists) {
      f.messages = r.exists;
      changed = true;
    }
    if (typeof r.unseen === 'number' && f.unseen !== r.unseen) {
      f.unseen = r.unseen;
      changed = true;
    }
    if (changed) {
      this.s.folders = [...this.s.folders];
      void db.folders.put(plainFolder(f)).catch(() => undefined);
    }
  }

  private async deleteRows(keys: string[]): Promise<void> {
    await db.messages.bulkDelete(keys);
    await db.bodies.bulkDelete(keys);
  }

  private async afterSync(folder: string): Promise<void> {
    if (this.s.currentFolder === folder) {
      await this.loadListFromCache(folder);
      await this.countLoaded(folder);
    }
    if (folder.toUpperCase() === 'INBOX') void this.s.loadSplitCounts();
  }

  /** Page older rows (`beforeSeq` = smallest cached seq). */
  async loadMore(): Promise<void> {
    if (this.loadingMore || this.s.offline || this.s.searchResults) return;
    const folder = this.s.currentFolder;
    if (this.s.folderTotal && this.s.folderLoaded >= this.s.folderTotal) return;
    this.loadingMore = true;
    try {
      const cached = await this.listedRows(folder);
      if (!cached.length) return;
      const minSeq = Math.min(...cached.map((r) => r.seq));
      if (minSeq <= 1) return;
      const r = await api.messages(folder, { limit: PAGE_SIZE, beforeSeq: minSeq });
      if (folder !== this.s.currentFolder) return;
      if (r.total) this.s.folderTotal = r.total;
      if (r.messages.length) {
        const rows = r.messages.map((m) => this.mergeRow(this.toRow(folder, m), cached));
        await db.messages.bulkPut(rows);
        if (folder !== this.s.currentFolder) return;
        await this.loadListFromCache(folder);
        await this.countLoaded(folder);
      }
    } catch {
      /* paging failures are silent; the tail button lets the user retry */
    } finally {
      this.loadingMore = false;
    }
  }

  // ---- previews -----------------------------------------------------------------------------

  ensurePreviews(uids: readonly number[]): void {
    if (this.s.offline) return;
    const folder = this.s.currentFolder;
    for (const uid of uids) {
      const key = rowKey(folder, uid);
      if (this.previewAsked.has(key) || this.s.msgs.find((m) => m.uid === uid)?.snippet) continue;
      this.previewAsked.add(key);
      this.previewQueue.push({ folder, uid });
    }
    if (this.previewQueue.length) void this.drainPreviews();
  }

  async drainPreviews(): Promise<void> {
    if (this.previewBusy) return;
    this.previewBusy = true;
    try {
      while (!this.s.offline) {
        const folder = this.s.currentFolder;
        const batch = this.previewQueue.filter((p) => p.folder === folder).slice(0, PREVIEW_BATCH);
        if (!batch.length) break;
        this.previewQueue = this.previewQueue.filter((p) => !batch.includes(p));
        const uids = batch.map((p) => p.uid);
        let previews: { uid: number; snippet: string }[] = [];
        try {
          ({ previews } = await api.previews(folder, uids));
        } catch {
          for (const uid of uids) this.previewAsked.delete(rowKey(folder, uid));
          break;
        }
        const got = new Set(previews.map((p) => p.uid));
        for (const uid of uids) if (!got.has(uid)) this.previewAsked.delete(rowKey(folder, uid));
        const snippets = new Map(previews.filter((p) => p.snippet).map((p) => [rowKey(folder, p.uid), p.snippet]));
        if (!snippets.size) continue;
        const rows = (await db.messages.bulkGet([...snippets.keys()])).filter((r): r is MessageRow => !!r).map((r) => ({ ...r, snippet: snippets.get(r.key) }));
        await db.messages.bulkPut(rows);
        this.s.msgs = this.s.msgs.map((m) => (snippets.has(m.key) ? { ...m, snippet: snippets.get(m.key) } : m));
      }
    } catch {
      /* drained on the next call */
    } finally {
      this.previewBusy = false;
    }
  }

  // ---- thread rows, cursor, selection --------------------------------------------------------

  computeRows(): ThreadRow[] {
    const base = this.s.searchResults ?? this.s.msgs;
    if (this.s.searchResults || !this.s.threadView) return base.map(singleRow);
    return buildThreadRows(base);
  }

  threadOf(uid: number, folder = this.s.currentFolder): MessageRow[] {
    return threadOfRows(this.s.msgs, uid, folder);
  }

  /** Uids acted on for a row's thread when thread view is on and the row is in this folder. */
  threadUids(uid: number, folder = this.s.currentFolder): number[] {
    if (!this.s.threadView) return [uid];
    const row = this.s.rows.find((r) => r.uids.includes(uid));
    return row && row.folder === folder && row.uids.length ? row.uids : [uid];
  }

  moveCursor(delta: number): void {
    const rows = this.s.rows;
    if (!rows.length) return;
    let i = rows.findIndex((r) => r.key === this.s.cursorKey);
    i = i < 0 ? Math.min(this.s.lastCursorIndex, rows.length - 1) : i + delta;
    i = Math.max(0, Math.min(rows.length - 1, i));
    this.s.cursorKey = rows[i]!.key;
    this.s.lastCursorIndex = i;
  }

  cursorTo(where: 'start' | 'end'): void {
    const rows = this.s.rows;
    if (!rows.length) return;
    const i = where === 'start' ? 0 : rows.length - 1;
    this.s.cursorKey = rows[i]!.key;
    this.s.lastCursorIndex = i;
  }

  /** The cursor row, falling back to the thread row that contains the cursor's uid. */
  cursorRow(): ThreadRow | null {
    const rows = this.s.rows;
    if (!rows.length) return null;
    const exact = rows.find((r) => r.key === this.s.cursorKey);
    if (exact) return exact;
    const uid = parseRowKey(this.s.cursorKey)?.uid ?? NaN;
    const byUid = Number.isFinite(uid) ? rows.find((r) => r.uids.includes(uid)) : undefined;
    if (!byUid) return null;
    this.s.cursorKey = byUid.key;
    return byUid;
  }

  cursorUid(): number | null {
    return this.cursorRow()?.uid ?? null;
  }

  cursorUids(): number[] {
    const r = this.cursorRow();
    return r ? (r.uids.length ? r.uids : [r.uid]) : [];
  }

  toggleRow(row: ThreadRow): void {
    const uids = row.uids?.length ? row.uids : [row.uid];
    const has = this.s.selection.has(row.uid);
    if (!has && this.s.selection.size === 0) this.s.selectionFolder = this.s.currentFolder;
    for (const u of uids) {
      if (has) this.s.selection.delete(u);
      else this.s.selection.add(u);
    }
    if (this.s.selection.size === 0) this.s.selectionFolder = null;
  }

  toggleSelect(uid: number): void {
    if (this.s.selection.has(uid)) this.s.selection.delete(uid);
    else {
      if (this.s.selection.size === 0) this.s.selectionFolder = this.s.currentFolder;
      this.s.selection.add(uid);
    }
    if (this.s.selection.size === 0) this.s.selectionFolder = null;
  }

  clearSelection(): void {
    this.s.selection.clear();
    this.s.selectionFolder = null;
  }

  selectOnly(uid: number): void {
    this.s.selection.clear();
    this.s.selection.add(uid);
    this.s.selectionFolder = this.s.currentFolder;
  }

  selectAllShown(): void {
    const rows = this.s.rows;
    if (!rows.length) return;
    this.s.selectionFolder = this.s.currentFolder;
    for (const r of rows) for (const u of r.uids.length ? r.uids : [r.uid]) this.s.selection.add(u);
    this.s.lastAnchorKey = rows[0]!.key;
  }

  selectWhere(pred: (flags: string[]) => boolean): number {
    const rows = this.s.rows;
    this.s.selection.clear();
    this.s.selectionFolder = null;
    const hits = rows.filter((r) => pred(r.flags || []));
    if (!hits.length) return 0;
    this.s.selectionFolder = this.s.currentFolder;
    for (const r of hits) for (const u of r.uids.length ? r.uids : [r.uid]) this.s.selection.add(u);
    this.s.lastAnchorKey = hits[0]!.key;
    return hits.length;
  }

  selectRangeTo(row: ThreadRow): void {
    const rows = this.s.rows;
    const to = rows.findIndex((r) => r.key === row.key);
    const from = this.s.lastAnchorKey ? rows.findIndex((r) => r.key === this.s.lastAnchorKey) : -1;
    if (to < 0 || from < 0) {
      this.toggleRow(row);
      this.s.lastAnchorKey = row.key;
      return;
    }
    const [a, b] = from <= to ? [from, to] : [to, from];
    if (this.s.selection.size === 0) this.s.selectionFolder = this.s.currentFolder;
    for (let i = a; i <= b; i++) for (const u of rows[i]!.uids.length ? rows[i]!.uids : [rows[i]!.uid]) this.s.selection.add(u);
    this.s.lastAnchorKey = row.key;
  }

  /** Clears a selection that belongs to another folder; returns whether it did. */
  staleSelection(): boolean {
    if (this.s.selectionFolder && this.s.selectionFolder !== this.s.currentFolder) {
      this.clearSelection();
      return true;
    }
    return false;
  }

  // ---- reader navigation --------------------------------------------------------------------

  readerPos(): { i: number; total: number } | null {
    const sc = this.s.screen;
    if (sc.name !== 'read') return null;
    const rows = this.s.rows;
    const i = rows.findIndex((r) => r.folder === sc.folder && (r.uid === sc.uid || (r.uids ?? []).includes(sc.uid)));
    if (i < 0) return null;
    return { i, total: this.s.searchResults ? rows.length : Math.max(this.s.folderTotal || 0, rows.length) };
  }

  /** Row to open after a "leave" action per `autoAdvance`; null → back to the list. */
  nextAfter(): { uid: number; folder: string; key: string } | null {
    if (this.s.autoAdvance === 'list') return null;
    const pos = this.readerPos();
    if (!pos) return null;
    const rows = this.s.rows;
    const dir = this.s.autoAdvance === 'next' ? 1 : -1;
    const r = rows[pos.i + dir] ?? rows[pos.i - dir];
    return r ? { uid: r.uid, folder: r.folder, key: r.key } : null;
  }

  async openNeighbor(delta: number): Promise<boolean> {
    const pos = this.readerPos();
    if (!pos) return false;
    let rows = this.s.rows;
    const i = pos.i + delta;
    if (i >= rows.length && !this.s.searchResults && this.s.folderTotal > this.s.folderLoaded) {
      await this.loadMore();
      rows = this.s.rows;
    }
    if (i < 0 || i >= rows.length) return false;
    const r = rows[i]!;
    this.s.cursorKey = r.key;
    void this.s.openMessage(r.uid, r.folder);
    return true;
  }
}

/** Folder record without the reactive proxy (Dexie needs a plain object). */
export function plainFolder(f: FolderRec): FolderRec {
  return { path: f.path, name: f.name, specialUse: f.specialUse, delimiter: f.delimiter ?? undefined, messages: f.messages, unseen: f.unseen, modseq: f.modseq ?? null };
}
